import { Injectable } from '@angular/core';
import { Endpoint, MetadataTokenizationType, SettingsType } from '../../models/enums';
import { RestService } from '../rest/rest.service';

export class MetadataToken {
    name: string;
    tokenType: MetadataTokenizationType;
    category: string;
    description: string;

    constructor(value?: Partial<MetadataToken>) {
        if (value) {
            Object.assign(this, value);
        }
    }
}

export class MetadataBulkField {
    name: string;
    displayName: string;

    constructor(value?: Partial<MetadataBulkField>) {
        if (value) {
            Object.assign(this, value);
        }
    }
}

interface InvalidMetadataResult {
    invalidMetadata: string;
    fullMatch: string;
}

@Injectable()
export class MetadataService {

    private _fieldDescriptions: [];
    public get fieldDescriptions(): () => Promise<any> {
        const optionalCall = !this._fieldDescriptions ? (() => Promise.resolve(
            this.restService.post(Endpoint.realmManager_getMetadataFieldDescriptions, { containerId: 0 }).then(responseDescriptions =>
                this._fieldDescriptions = responseDescriptions?.descriptions
            ))) : () => Promise.resolve(this._fieldDescriptions);

        return optionalCall;

    }

    private _tokenizationTypes: [];
    public get tokenizationTypes(): () => Promise<Array<MetadataToken>> {
        const optionalCall = !this._tokenizationTypes ? (() => Promise.resolve(
            this.restService.post(Endpoint.realmManager_getMetadataFieldProperties, { containerId: 0 }).then(responseTokenization =>
                this._tokenizationTypes = responseTokenization?.metadataFieldProperties
            ))) : () => Promise.resolve(this._tokenizationTypes);

        return optionalCall;
    }

    private __metadataBulkFields: [];
    public get metadataBulkFields(): () => Promise<Array<MetadataBulkField>> {
        const optionalCall = !this.__metadataBulkFields ? (() => Promise.resolve(
            this.restService.post(Endpoint.realmManager_listMetadataBulkFields, {}).then(response =>
                this.__metadataBulkFields = response?.metadataBulkFields
            ))) : () => Promise.resolve(this.__metadataBulkFields);

        return optionalCall;
    }

    private _customMetadataFieldsInProject: Array<string> = [];
    public get customMetadataFieldsInProject(): Array<string> {
        return this._customMetadataFieldsInProject;
    }
    public set customMetadataFieldsInProject(value: Array<string>) {
        if (value) {
            this._customMetadataFieldsInProject = value;
        }
    }

    private _customMetadataFieldsInOrganization: Array<string> = [];
    public get customMetadataFieldsInOrganization(): Array<string> {
        return this._customMetadataFieldsInOrganization;
    }
    public set customMetadataFieldsInOrganization(value: Array<string>) {
        if (value) {
            this._customMetadataFieldsInOrganization = value;
        }
    }

    public getCustomMetadataFields(settingsType: SettingsType, projectHandle: string, organizationName: string): void {
        let request = { projectHandle: null, orgName: null };
        if (settingsType === SettingsType.project) {
            request["projectHandle"] = projectHandle;
        } else if (settingsType === SettingsType.organization) {
            request["orgName"] = organizationName;
        }

        this.restService.post(Endpoint.projectManager_getCustomFields, request).then(customMetadataFieldsResponse => {
            if (customMetadataFieldsResponse) {
                if (settingsType === SettingsType.project) {
                    this.customMetadataFieldsInProject = customMetadataFieldsResponse?.entityNames;
                } else if (settingsType === SettingsType.organization) {
                    this.customMetadataFieldsInOrganization = customMetadataFieldsResponse?.entityNames;
                }
            }
        });
    }

    constructor(private restService: RestService) { }

    public findInvalidMetadataFields(text: string, validMetadataFields: any[], settingsType: SettingsType): InvalidMetadataResult[] {
        const results: InvalidMetadataResult[] = [];

        const validMetadataNames = new Set(validMetadataFields.map(field => field.name.toLowerCase()));

        // Add custom metadata fields for validation
        if (settingsType === SettingsType.project) {
            this.customMetadataFieldsInProject.forEach(customMetadataField => validMetadataNames.add(customMetadataField.toLowerCase()));
        } else if (settingsType === SettingsType.organization) {
            this.customMetadataFieldsInOrganization.forEach(customMetadataField => validMetadataNames.add(customMetadataField.toLowerCase()));
        }

        // Match any sequence (not including space, colon, parenthesis) before ::
        const metadataRegex = /([^\s:()]+)::/g;

        let match;

        while ((match = metadataRegex.exec(text)) !== null) {
            const metadataName = match[1].replace(/^\(+/, '').replace(/\)+$/, '').trim(); // The part before '::' & removing leading/trailing parentheses and spaces
            const fullMatch = `${metadataName}::`; // The complete match including '::'

            // Check if this metadata name is NOT in our valid list
            if (!validMetadataNames.has(metadataName.toLowerCase())) {
                results.push({
                    invalidMetadata: metadataName,
                    fullMatch: fullMatch,
                });
            }
        }

        return results;
    }
}
